/**
 * VerifyPage - Pagina di verifica Magic Link
 * 
 * Gestisce il callback quando l'utente clicca sul Magic Link.
 * Verifica il token e reindirizza alla pagina del viaggio.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, RefreshCw, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SESSION_TOKEN_KEY = 'fu_verified_session';

export default function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [sessionData, setSessionData] = useState(null);

  // Extract params from URL
  const code = searchParams.get('code');
  const token = searchParams.get('token');
  const purpose = searchParams.get('purpose');
  const signature = searchParams.get('sig');

  useEffect(() => {
    const verifyToken = async () => {
      // Validate params
      if (!code || !token || !purpose || !signature) {
        setStatus('error');
        setMessage('Link non valido. Parametri mancanti.');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/magic-link/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            journey_code: code,
            token,
            purpose,
            signature
          })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Store session token
          try {
            sessionStorage.setItem(`${SESSION_TOKEN_KEY}_${code}`, data.session_token);
          } catch (err) {
            console.error('Failed to store session:', err);
          }

          setStatus('success');
          setSessionData(data.session);
          setMessage('Verifica completata con successo!');

          // Auto-redirect after 2 seconds
          setTimeout(() => {
            navigate(`/il-mio-viaggio/${code}?verified=true`);
          }, 2000);
        } else {
          setStatus('error');
          setMessage(data.detail || data.message || 'Verifica fallita');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setStatus('error');
        setMessage('Errore di connessione. Riprova più tardi.');
      }
    };

    verifyToken();
  }, [code, token, purpose, signature, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-umbria-cream to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-umbria-olive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <RefreshCw size={32} className="text-umbria-olive animate-spin" />
              </div>
              <h1 className="text-xl font-semibold text-umbria-charcoal mb-2">
                Verifica in corso...
              </h1>
              <p className="text-umbria-muted">
                Stiamo verificando il tuo link
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h1 className="text-xl font-semibold text-green-800 mb-2">
                {message}
              </h1>
              <p className="text-green-600 mb-6">
                Accesso verificato per {sessionData?.minutes_remaining || 120} minuti
              </p>
              
              <div className="bg-green-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <Shield size={18} />
                  <span className="font-medium">Accesso verificato</span>
                </div>
                <p className="text-sm text-green-600 mt-1">
                  Ora puoi accedere alle azioni sensibili
                </p>
              </div>

              <p className="text-sm text-umbria-muted mb-4">
                Reindirizzamento automatico...
              </p>
              
              <Link to={`/il-mio-viaggio/${code}`}>
                <Button className="bg-umbria-olive hover:bg-umbria-olive/90">
                  Vai al viaggio
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle size={32} className="text-red-600" />
              </div>
              <h1 className="text-xl font-semibold text-red-800 mb-2">
                Verifica fallita
              </h1>
              <p className="text-red-600 mb-6">
                {message}
              </p>
              
              <div className="space-y-3">
                {code && (
                  <Link to={`/il-mio-viaggio/${code}`}>
                    <Button variant="outline" className="w-full">
                      Torna al viaggio
                    </Button>
                  </Link>
                )}
                
                <p className="text-xs text-umbria-muted">
                  Il link potrebbe essere scaduto o già utilizzato.
                  <br />
                  Richiedi un nuovo link dalla pagina del viaggio.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
